/**
 * Gap Aggregation Service
 * Handles semantic deduplication, similarity matching, and gap grouping.
 */

import type { Gap, SmartGap, GapGroup, GapPriority, GapStats } from '@/types/agent.types';

/**
 * Normalize text for comparison (lowercase, trim, remove extra spaces)
 */
function normalizeText(text: string): string {
  return text.toLowerCase().trim().replace(/\s+/g, ' ');
}

/**
 * Portuguese stop words to ignore in question comparison
 */
const QUESTION_STOP_WORDS = new Set([
  'o', 'a', 'os', 'as', 'um', 'uma', 'de', 'do', 'da', 'dos', 'das',
  'que', 'para', 'e', 'em', 'no', 'na', 'nos', 'nas', 'qual', 'quais',
  'como', 'por', 'pelo', 'pela', 'ser', 'vai', 'será', 'é'
]);

/**
 * Normalize a question for comparison.
 * - Lowercase
 * - Remove punctuation (?, !, .)
 * - Collapse whitespace
 * - Remove common Portuguese stop words
 */
function normalizeQuestion(question: string): string {
  return question
    .toLowerCase()
    .replace(/[?!.,;:'"]/g, '')
    .trim()
    .replace(/\s+/g, ' ')
    .split(' ')
    .filter(word => word.length > 2 && !QUESTION_STOP_WORDS.has(word))
    .join(' ');
}

/**
 * Calculate similarity between two questions using Jaccard similarity.
 */
function questionSimilarity(q1: string, q2: string): number {
  const norm1 = normalizeQuestion(q1);
  const norm2 = normalizeQuestion(q2);

  const words1 = new Set(norm1.split(' ').filter(w => w.length > 0));
  const words2 = new Set(norm2.split(' ').filter(w => w.length > 0));

  if (words1.size === 0 && words2.size === 0) return 1;
  if (words1.size === 0 || words2.size === 0) return 0;

  const intersection = new Set([...words1].filter(w => words2.has(w)));
  const union = new Set([...words1, ...words2]);

  return intersection.size / union.size;
}

/**
 * Check if two questions are semantically similar.
 * Threshold: 0.75 (75% word overlap)
 */
export function areQuestionsSimilar(q1: string, q2: string, threshold: number = 0.75): boolean {
  return questionSimilarity(q1, q2) >= threshold;
}

/**
 * Deduplicate an array of questions, keeping only unique ones.
 * Returns the first occurrence of each unique question.
 */
export function deduplicateQuestions(questions: string[], threshold: number = 0.75): string[] {
  if (questions.length <= 1) return questions;

  const unique: string[] = [];

  for (const question of questions) {
    const isDuplicate = unique.some(existing =>
      areQuestionsSimilar(existing, question, threshold)
    );

    if (!isDuplicate) {
      unique.push(question);
    }
  }

  return unique;
}

/**
 * Merge two arrays of questions, deduplicating similar ones.
 */
export function mergeQuestions(existing: string[], incoming: string[], threshold: number = 0.75): string[] {
  const combined = [...existing, ...incoming];
  return deduplicateQuestions(combined, threshold);
}

/**
 * Compute a semantic key for gap deduplication.
 * Uses MD5-like hash of section + description.
 */
export function computeSemanticKey(section: string, description: string): string {
  const content = `${normalizeText(section)}:${normalizeText(description)}`;
  // Simple hash function (djb2)
  let hash = 5381;
  for (let i = 0; i < content.length; i++) {
    hash = (hash * 33) ^ content.charCodeAt(i);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
}

/**
 * Calculate Jaccard similarity between two strings.
 * Returns 0.0 - 1.0
 */
function jaccardSimilarity(str1: string, str2: string): number {
  const words1 = new Set(normalizeText(str1).split(' ').filter(w => w.length > 2));
  const words2 = new Set(normalizeText(str2).split(' ').filter(w => w.length > 2));

  if (words1.size === 0 && words2.size === 0) return 1;
  if (words1.size === 0 || words2.size === 0) return 0;

  const intersection = new Set([...words1].filter(w => words2.has(w)));
  const union = new Set([...words1, ...words2]);

  return intersection.size / union.size;
}

/**
 * Calculate similarity between two gaps.
 * Returns 0.0 - 1.0
 */
export function calculateSimilarity(gap1: Gap, gap2: Gap | SmartGap): number {
  // Section match weight: 0.3
  const sectionScore = normalizeText(gap1.section) === normalizeText(gap2.section) ? 1 : 0;

  // Description similarity weight: 0.5
  const descriptionScore = jaccardSimilarity(gap1.description, gap2.description);

  // Suggested questions overlap weight: 0.2
  let questionsScore = 0;
  if (gap1.suggested_questions.length > 0 && gap2.suggested_questions.length > 0) {
    const q1 = new Set(gap1.suggested_questions.map(normalizeText));
    const q2 = new Set(gap2.suggested_questions.map(normalizeText));
    const intersection = [...q1].filter(q => {
      return [...q2].some(q2Item => jaccardSimilarity(q, q2Item) > 0.7);
    });
    questionsScore = intersection.length / Math.max(q1.size, q2.size);
  }

  return sectionScore * 0.3 + descriptionScore * 0.5 + questionsScore * 0.2;
}

/**
 * Find a matching gap in existing list based on semantic similarity.
 * Returns the matching gap or null if no match found.
 */
export function findMatchingGap(
  newGap: Gap,
  existingGaps: SmartGap[],
  threshold: number = 0.7
): SmartGap | null {
  for (const existing of existingGaps) {
    const similarity = calculateSimilarity(newGap, existing);
    if (similarity >= threshold) {
      return existing;
    }
  }
  return null;
}

/**
 * Merge an incoming gap into an existing SmartGap.
 * Increments detection count, updates confidence score, and merges questions.
 */
export function mergeGaps(existing: SmartGap, incoming: Gap): SmartGap {
  const newCount = existing.detectionCount + 1;
  // Confidence increases with each detection (asymptotic to 1.0)
  const newConfidence = Math.min(0.95, existing.confidenceScore + (1 - existing.confidenceScore) * 0.15);

  // Merge and deduplicate suggested questions
  const mergedQuestions = mergeQuestions(
    existing.suggested_questions,
    incoming.suggested_questions,
    0.75
  );

  return {
    ...existing,
    detectionCount: newCount,
    confidenceScore: newConfidence,
    lastDetectedAt: new Date().toISOString(),
    // Keep highest priority
    priority: getPriorityRank(incoming.priority) > getPriorityRank(existing.priority)
      ? incoming.priority
      : existing.priority,
    // Update with merged and deduplicated questions
    suggested_questions: mergedQuestions,
  };
}

/**
 * Convert a Gap to SmartGap with initial metadata.
 * Deduplicates suggested questions on creation.
 */
export function createSmartGap(gap: Gap): SmartGap {
  const now = new Date().toISOString();
  return {
    ...gap,
    // Deduplicate questions in case backend sends duplicates
    suggested_questions: deduplicateQuestions(gap.suggested_questions, 0.75),
    detectionCount: 1,
    confidenceScore: 0.5,
    firstDetectedAt: now,
    lastDetectedAt: now,
    semanticKey: computeSemanticKey(gap.section, gap.description),
  };
}

/**
 * Get priority rank for comparison (higher = more urgent)
 */
function getPriorityRank(priority: GapPriority): number {
  switch (priority) {
    case 'alta': return 3;
    case 'média': return 2;
    case 'baixa': return 1;
    default: return 0;
  }
}

/**
 * Sort gaps by various criteria.
 */
export function sortGaps(
  gaps: SmartGap[],
  sortBy: 'priority' | 'confidence' | 'detectionCount' | 'lastDetected',
  sortOrder: 'asc' | 'desc' = 'desc'
): SmartGap[] {
  const sorted = [...gaps].sort((a, b) => {
    let comparison = 0;

    switch (sortBy) {
      case 'priority':
        comparison = getPriorityRank(a.priority) - getPriorityRank(b.priority);
        break;
      case 'confidence':
        comparison = a.confidenceScore - b.confidenceScore;
        break;
      case 'detectionCount':
        comparison = a.detectionCount - b.detectionCount;
        break;
      case 'lastDetected':
        comparison = new Date(a.lastDetectedAt).getTime() - new Date(b.lastDetectedAt).getTime();
        break;
    }

    return sortOrder === 'desc' ? -comparison : comparison;
  });

  return sorted;
}

/**
 * Group gaps by section.
 */
export function groupGapsBySection(gaps: SmartGap[]): GapGroup[] {
  const groups = new Map<string, SmartGap[]>();

  for (const gap of gaps) {
    const section = gap.section;
    if (!groups.has(section)) {
      groups.set(section, []);
    }
    groups.get(section)!.push(gap);
  }

  const result: GapGroup[] = [];
  for (const [section, sectionGaps] of groups) {
    const unresolvedCount = sectionGaps.filter(g => !g.resolved).length;
    const highPriorityCount = sectionGaps.filter(g => g.priority === 'alta' && !g.resolved).length;

    result.push({
      section,
      gaps: sortGaps(sectionGaps, 'priority', 'desc'),
      unresolvedCount,
      highPriorityCount,
    });
  }

  // Sort groups by high priority count, then by unresolved count
  result.sort((a, b) => {
    if (a.highPriorityCount !== b.highPriorityCount) {
      return b.highPriorityCount - a.highPriorityCount;
    }
    return b.unresolvedCount - a.unresolvedCount;
  });

  return result;
}

/**
 * Calculate gap statistics.
 */
export function calculateGapStats(gaps: SmartGap[]): GapStats {
  return {
    total: gaps.length,
    unresolved: gaps.filter(g => !g.resolved).length,
    highPriority: gaps.filter(g => g.priority === 'alta' && !g.resolved).length,
    resolved: gaps.filter(g => g.resolved).length,
  };
}

/**
 * Filter gaps based on filter options.
 */
export function filterGaps(
  gaps: SmartGap[],
  filters: {
    sections?: string[];
    priorities?: GapPriority[];
    showResolved?: boolean;
    searchQuery?: string;
  }
): SmartGap[] {
  return gaps.filter(gap => {
    // Section filter
    if (filters.sections && filters.sections.length > 0) {
      if (!filters.sections.includes(gap.section)) return false;
    }

    // Priority filter
    if (filters.priorities && filters.priorities.length > 0) {
      if (!filters.priorities.includes(gap.priority)) return false;
    }

    // Resolved filter
    if (filters.showResolved === false && gap.resolved) {
      return false;
    }

    // Search query
    if (filters.searchQuery && filters.searchQuery.trim()) {
      const query = filters.searchQuery.toLowerCase();
      const matchesSection = gap.section.toLowerCase().includes(query);
      const matchesDescription = gap.description.toLowerCase().includes(query);
      const matchesQuestions = gap.suggested_questions.some(q =>
        q.toLowerCase().includes(query)
      );
      if (!matchesSection && !matchesDescription && !matchesQuestions) {
        return false;
      }
    }

    return true;
  });
}

/**
 * Get all unique sections from gaps.
 */
export function getUniqueSections(gaps: SmartGap[]): string[] {
  const sections = new Set(gaps.map(g => g.section));
  return Array.from(sections).sort();
}

/**
 * Mapping of document fields to related gap sections.
 * Used for auto-resolving gaps when fields are filled.
 */
const fieldToSectionMap: Record<string, string[]> = {
  // Client information
  cliente: ['Cliente', 'Dados do Cliente', 'Informações do Cliente'],
  objetivo: ['Objetivo', 'Objetivos', 'Requisitos', 'Escopo'],

  // System A
  sistema_a_nome: ['Sistema A', 'Sistemas', 'Sistema Origem'],
  sistema_a_funcao: ['Sistema A', 'Sistemas', 'Funcionalidades'],
  sistema_a_tecnologia: ['Sistema A', 'Tecnologia', 'Stack'],
  sistema_a_versao: ['Sistema A', 'Versão', 'Versões'],

  // System B
  sistema_b_nome: ['Sistema B', 'Sistemas', 'Sistema Destino'],
  sistema_b_funcao: ['Sistema B', 'Sistemas', 'Funcionalidades'],
  sistema_b_tecnologia: ['Sistema B', 'Tecnologia', 'Stack'],
  sistema_b_versao: ['Sistema B', 'Versão', 'Versões'],

  // Connection A
  conexao_a_metodo: ['Conexão', 'Conexão A', 'Método de Conexão', 'API'],
  conexao_a_endpoint: ['Conexão', 'Conexão A', 'Endpoints', 'API'],
  conexao_a_autenticacao: ['Conexão', 'Conexão A', 'Autenticação', 'Segurança'],
  conexao_a_formato: ['Conexão', 'Conexão A', 'Formato de Dados', 'Dados'],

  // Connection B
  conexao_b_metodo: ['Conexão', 'Conexão B', 'Método de Conexão', 'API'],
  conexao_b_endpoint: ['Conexão', 'Conexão B', 'Endpoints', 'API'],
  conexao_b_autenticacao: ['Conexão', 'Conexão B', 'Autenticação', 'Segurança'],
  conexao_b_formato: ['Conexão', 'Conexão B', 'Formato de Dados', 'Dados'],

  // Data flow
  fluxo_dados: ['Fluxo', 'Fluxo de Dados', 'Dados', 'Mapeamento'],
  mapeamento_campos: ['Mapeamento', 'Campos', 'Dados', 'Transformação'],
  transformacoes: ['Transformação', 'Transformações', 'Dados', 'Regras'],

  // Error handling
  tratamento_erros: ['Erros', 'Tratamento de Erros', 'Exceções'],
  retentativas: ['Retentativas', 'Retry', 'Erros', 'Resiliência'],
  notificacoes: ['Notificações', 'Alertas', 'Monitoramento'],

  // Schedule and frequency
  frequencia: ['Frequência', 'Agendamento', 'Periodicidade'],
  horario: ['Horário', 'Agendamento', 'Janela'],

  // Requirements
  requisitos_nfr: ['Requisitos', 'Requisitos Não-Funcionais', 'NFR'],
  volume_dados: ['Volume', 'Performance', 'Capacidade'],
  sla: ['SLA', 'Requisitos', 'Acordos'],
};

/**
 * Get related sections for a given field name.
 */
export function getFieldSections(fieldName: string): string[] {
  return fieldToSectionMap[fieldName] || [];
}

/**
 * Check if a gap's section is related to any of the provided sections.
 */
export function isSectionRelated(gapSection: string, fieldSections: string[]): boolean {
  if (fieldSections.length === 0) return false;

  const normalizedGapSection = gapSection.toLowerCase().trim();

  return fieldSections.some((fs) => {
    const normalizedFieldSection = fs.toLowerCase().trim();
    return (
      normalizedGapSection.includes(normalizedFieldSection) ||
      normalizedFieldSection.includes(normalizedGapSection)
    );
  });
}

/**
 * Find gaps that should be auto-resolved when a field is filled.
 * Returns the IDs of gaps to resolve.
 */
export function findGapsToAutoResolve(
  fieldName: string,
  gaps: SmartGap[]
): string[] {
  const fieldSections = getFieldSections(fieldName);
  if (fieldSections.length === 0) return [];

  return gaps
    .filter((gap) => !gap.resolved && isSectionRelated(gap.section, fieldSections))
    .map((gap) => gap.id);
}
